import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AppUser, Entitlement } from '../model/types';
import { EMPTY_ENTITLEMENT } from '../model/plans';
import { BACKEND } from '../config';
import type { Backend, PublicClient } from './types';

let loading: Promise<Backend> | null = null;
/** Loads the backend once. Only the one in use is downloaded. */
export function loadBackend(): Promise<Backend> {
  if (!loading) {
    loading = BACKEND === 'api'
      ? import('./api').then((m) => m.createApiBackend())
      : import('./demo').then((m) => m.createDemoBackend());
  }
  return loading;
}

/** What a guest's page needs. With the real backend this skips Firebase entirely. */
export function loadPublicClient(): Promise<PublicClient> {
  return BACKEND === 'api' ? import('./public').then((m) => m.createApiPublicClient()) : loadBackend();
}

/** Set while someone logs out on purpose, so they land on the home page rather than being asked to log in again. */
export const loggingOut = { current: false };

interface Session { backend: Backend; user: AppUser | null; ready: boolean; entitlement: Entitlement; entitlementReady: boolean }
const Ctx = createContext<Session | null>(null);

export function BackendProvider({ backend, children }: { backend: Backend; children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [ready, setReady] = useState(false);
  const [ent, setEnt] = useState<{ e: Entitlement; ready: boolean }>({ e: EMPTY_ENTITLEMENT, ready: false });

  useEffect(() => backend.onAuthChange((u) => { setUser(u); setReady(true); }), [backend]);
  const uid = user ? user.uid : null;
  useEffect(() => {
    setEnt({ e: EMPTY_ENTITLEMENT, ready: !uid });
    if (!uid) return;
    return backend.watchEntitlement((e) => setEnt({ e, ready: true }));
  }, [backend, uid]);

  return <Ctx.Provider value={{ backend, user, ready, entitlement: ent.e, entitlementReady: ent.ready }}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside BackendProvider');
  return s;
}
export const useBackend = () => useSession().backend;
